import {Injectable, inject} from "@angular/core";
import {HttpClient, HttpParams} from "@angular/common/http";
import {Observable, map} from "rxjs";
import {ThreePLServiceDetails} from "../models/threepl-service-details.model";
import {ThreePLServicesListQuery, ThreePLServicesTotals} from "../models/threepl-services-list.model";
import {ExportType} from "../../../commons/types/export.type";
import {toCursorPage} from "./threepl-train-http.service";
import {TimelineLog} from "../../../commons/angular/components/bringoz-timeline-logs-table-row/bringoz-timeline-logs-table-row.component";

export function unwrapBringozList<T>(response: unknown): T {
  const envelope = response as {list?: unknown; cursor?: unknown} | null;
  const isListEnvelope = !!envelope && typeof envelope === "object" && !Array.isArray(envelope)
    && (typeof envelope.cursor === "string" || (!!envelope.list && Object.keys(envelope).length === 1));
  return (isListEnvelope ? envelope.list ?? [] : response) as T;
}

export interface ThreePLServicesPage {
  list: ThreePLServiceDetails[];
  cursor: string;
}

export interface ThreePLServicesExportQuery {
  group?: string;
  from: number;
  to: number;
  depotId?: string[];
  accountId?: string[];
  branchId?: string[];
  areaId?: string[];
  providerType?: string[];
  supplyAccountId?: string[];
  serviceTypes?: string[];
  programType?: string[];
  searchTerm?: string;
  id?: string[];
  zoneId: string;
  type: ExportType;
}

export interface ThreePLServicesExportPayload {
  fieldTranslationMap: Record<string, string>;
  fieldsMap: Record<string, boolean>;
}

export type ThreePLServicesTotalsQuery = Pick<ThreePLServicesExportQuery,
  "from" | "to" | "depotId" | "accountId" | "branchId" | "areaId" | "providerType" | "supplyAccountId" | "serviceTypes" | "programType">;

type QueryValue = string | number | boolean | readonly (string | number)[] | undefined | null;

export function toHttpParams(query: object): HttpParams {
  return Object.entries(query as Record<string, QueryValue>).reduce((params, [key, value]) => {
    if (value === undefined || value === null) {
      return params;
    }
    return Array.isArray(value)
      ? value.reduce((acc: HttpParams, item: string | number) => acc.append(key, String(item)), params)
      : params.set(key, String(value));
  }, new HttpParams());
}

@Injectable({providedIn: "root"})
export class ThreePLServiceHttpService {
  private readonly baseUrl = "/thirdPartyService";
  private readonly http = inject(HttpClient);

  list(query: ThreePLServicesListQuery, cursor?: string): Observable<ThreePLServicesPage> {
    return this.http.get<unknown>(this.baseUrl, {params: toHttpParams({...query, cursor})})
      .pipe(map((response) => toCursorPage<ThreePLServiceDetails>(response)));
  }

  getTotals(query: ThreePLServicesTotalsQuery): Observable<ThreePLServicesTotals> {
    return this.http.get<ThreePLServicesTotals>(`${this.baseUrl}/totals/statuses`, {params: toHttpParams(query)});
  }

  export(query: ThreePLServicesExportQuery, payload: ThreePLServicesExportPayload): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/actions/export`, payload, {params: toHttpParams(query)});
  }

  getOne(serviceId: string): Observable<ThreePLServiceDetails> {
    return this.http.get<ThreePLServiceDetails>(`${this.baseUrl}/${serviceId}`);
  }

  getTimeline(serviceId: string): Observable<TimelineLog[]> {
    return this.http.get<unknown>(`${this.baseUrl}/${serviceId}/timeline`).pipe(map((response) => unwrapBringozList<TimelineLog[]>(response)));
  }

  addNote(serviceIds: string[], note: unknown): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/actions/addNote`, note, {params: this.idParams(serviceIds)});
  }

  cancelService(serviceIds: string[], cancelReason: unknown): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/actions/cancel`, cancelReason, {params: this.idParams(serviceIds)});
  }

  private idParams(ids: string[]): HttpParams {
    return ids.reduce((params, id) => params.append("id", id), new HttpParams());
  }
}
